import React, { useState, useEffect, useRef } from 'react';
import { Bell, Check, CheckCheck, Megaphone, PenLine, ClipboardList, DollarSign, Award, FileText, MessageSquare } from 'lucide-react';
import api from '../lib/api';

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const fetchNotifications = async () => {
    try {
      const { data } = await api.get('/notifications');
      setNotifications(data);
      setUnreadCount(data.filter(n => !n.is_read).length);
    } catch {}
  };

  const fetchCount = async () => {
    try {
      const { data } = await api.get('/notifications/count');
      setUnreadCount(data.count);
    } catch {}
  };

  const markRead = async (id) => {
    try {
      await api.put(`/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await api.put('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch {}
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => { setOpen(!open); if (!open) fetchNotifications(); }}
        className="relative p-2 rounded-lg hover:bg-slate-100 transition-colors"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5 text-slate-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-12 w-80 sm:w-96 bg-white rounded-xl shadow-xl border border-slate-200 z-50 animate-scale-in" data-testid="notification-panel">
          <div className="flex items-center justify-between p-4 border-b border-slate-100">
            <h3 className="font-bold text-sm text-[#1B2A4A]">Notifications</h3>
            {unreadCount > 0 && (
              <button onClick={markAllRead} className="text-xs text-[#C9A84C] font-semibold hover:underline flex items-center gap-1" data-testid="mark-all-read-btn">
                <CheckCheck className="w-3 h-3" /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <p className="p-6 text-center text-sm text-slate-400">No notifications</p>
            ) : (
              notifications.slice(0, 20).map(n => (
                <div
                  key={n.id}
                  onClick={() => !n.is_read && markRead(n.id)}
                  className={`px-4 py-3 border-b border-slate-50 cursor-pointer hover:bg-slate-50 transition-colors ${!n.is_read ? 'bg-blue-50/50' : ''}`}
                  data-testid={`notification-item-${n.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${
                          n.type === 'assignment' ? 'bg-amber-100 text-amber-600' :
                          n.type === 'attendance' ? 'bg-sky-100 text-sky-600' :
                          n.type === 'invoice' ? 'bg-emerald-100 text-emerald-600' :
                          n.type === 'grade' ? 'bg-violet-100 text-violet-600' :
                          n.type === 'message' ? 'bg-blue-100 text-blue-600' :
                          'bg-slate-100 text-slate-500'
                        }`}>
                          {n.type === 'assignment' ? <PenLine className="w-3 h-3" /> :
                           n.type === 'attendance' ? <ClipboardList className="w-3 h-3" /> :
                           n.type === 'invoice' ? <DollarSign className="w-3 h-3" /> :
                           n.type === 'grade' ? <Award className="w-3 h-3" /> :
                           n.type === 'message' ? <MessageSquare className="w-3 h-3" /> :
                           <Megaphone className="w-3 h-3" />}
                        </span>
                        <p className={`text-sm ${!n.is_read ? 'font-bold text-[#1B2A4A]' : 'font-medium text-slate-600'}`}>{n.title}</p>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5 truncate pl-6.5">{n.message}</p>
                    </div>
                    {!n.is_read && <div className="w-2 h-2 rounded-full bg-[#C9A84C] flex-shrink-0 mt-1.5" />}
                  </div>
                  <p className="text-[10px] text-slate-400 mt-1">
                    {n.created_at ? (() => {
                      const diff = Date.now() - new Date(n.created_at).getTime();
                      const mins = Math.floor(diff / 60000);
                      if (mins < 1) return 'Just now';
                      if (mins < 60) return `${mins}m ago`;
                      const hrs = Math.floor(mins / 60);
                      if (hrs < 24) return `${hrs}h ago`;
                      const days = Math.floor(hrs / 24);
                      if (days < 7) return `${days}d ago`;
                      return new Date(n.created_at).toLocaleDateString();
                    })() : ''}
                  </p>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
